import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ErrorResponse, UserManager, User, WebStorageStateStore } from 'oidc-client-ts'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: () => Promise<void>
  logout: () => Promise<void>
  getAccessToken: () => string | null
  renewOrExpire: () => Promise<string | null>
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => {},
  logout: async () => {},
  getAccessToken: () => null,
  renewOrExpire: async () => null,
})

const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_URL || '/auth'
const KEYCLOAK_REALM = import.meta.env.VITE_KEYCLOAK_REALM || 'myrealm'
const KEYCLOAK_CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'admin-console-client'
const REDIRECT_URI = `${window.location.origin}/admin/callback`

// Only a path inside the console is honoured, never another origin or the callback itself.
function returnPathFrom(state: unknown): string {
  const returnTo = (state as { returnTo?: unknown } | null | undefined)?.returnTo
  if (typeof returnTo !== 'string') return '/admin/'
  if (!returnTo.startsWith('/admin/') || returnTo.startsWith('//') || returnTo.includes('\\')) return '/admin/'
  if (returnTo.startsWith('/admin/callback')) return '/admin/'
  return returnTo
}

const userManager = new UserManager({
  authority: `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}`,
  client_id: KEYCLOAK_CLIENT_ID,
  redirect_uri: REDIRECT_URI,
  post_logout_redirect_uri: `${window.location.origin}/admin/`,
  response_type: 'code',
  scope: 'openid profile email',
  automaticSilentRenew: true,
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const renewOrExpireRef = useRef<() => Promise<string | null>>(async () => null)

  useEffect(() => {
    // Handle callback from Keycloak
    if (window.location.pathname === '/admin/callback') {
      userManager
        .signinRedirectCallback()
        .then((u) => {
          setUser(u)
          window.history.replaceState({}, document.title, returnPathFrom(u.state))
          // BrowserRouter mounted on /admin/callback and only hears popstate; without this it
          // keeps routing /callback, whose catch-all sends every return to the dashboard.
          window.dispatchEvent(new PopStateEvent('popstate'))
        })
        .catch((err) => {
          console.error('Signin callback error:', err)
        })
        .finally(() => setIsLoading(false))
      return
    }

    // Try to get existing user (with timeout to prevent indefinite spinner)
    const AUTH_TIMEOUT_MS = 10_000
    Promise.race([
      userManager.getUser(),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), AUTH_TIMEOUT_MS)),
    ])
      .then((u) => {
        if (u && !u.expired) setUser(u)
      })
      .catch((err) => {
        console.error('Get user error:', err)
      })
      .finally(() => setIsLoading(false))

    // Listen for user changes
    const onUserLoaded = (u: User) => setUser(u)
    const onUserUnloaded = () => setUser(null)
    // Same single flight as the API client's 401 path, so the timer event and a 401 can never
    // run two refresh grants at once (a realm that rotates refresh tokens would revoke one).
    const onAccessTokenExpired = () => {
      void renewOrExpireRef.current()
    }
    const onSilentRenewError = () => setUser(null)

    userManager.events.addUserLoaded(onUserLoaded)
    userManager.events.addUserUnloaded(onUserUnloaded)
    userManager.events.addAccessTokenExpired(onAccessTokenExpired)
    userManager.events.addSilentRenewError(onSilentRenewError)

    return () => {
      userManager.events.removeUserLoaded(onUserLoaded)
      userManager.events.removeUserUnloaded(onUserUnloaded)
      userManager.events.removeAccessTokenExpired(onAccessTokenExpired)
      userManager.events.removeSilentRenewError(onSilentRenewError)
    }
  }, [])

  // Come back to the page that asked for the sign-in (2026-10-30: the MCP vault approval at
  // /admin/mcp-authorize?request=… was lost to the dashboard after Keycloak).
  const login = useCallback(async () => {
    const here = window.location.pathname + window.location.search
    await userManager.signinRedirect({ state: { returnTo: here } })
  }, [])

  const logout = useCallback(async () => {
    await userManager.signoutRedirect()
  }, [])

  // Never hand out a token we already know is expired: `isAuthenticated` below is memoised on
  // the user object, which does not change when the token lapses, so the pages kept polling
  // with it. A null token makes the request 401, and the client then calls renewOrExpire.
  const getAccessToken = useCallback(() => {
    return user && !user.expired ? user.access_token : null
  }, [user])

  // Single-flight silent renew for the API client's 401 path: ten polls failing at once
  // trigger one renew. Success -> the new token (userLoaded also updates state). Failure ->
  // drop the user, so the sign-in screen replaces the page and its polling stops.
  const renewRef = useRef<Promise<string | null> | null>(null)
  const renewOrExpire = useCallback(() => {
    if (!renewRef.current) {
      renewRef.current = userManager
        .signinSilent()
        .then((u) => {
          if (u && !u.expired) {
            setUser(u)
            return u.access_token
          }
          setUser(null)
          return null
        })
        .catch((err: unknown) => {
          // Only an answer from Keycloak (login_required, invalid_grant, …) ends the session. A
          // network blip keeps the user (and any unsaved form); this request fails and the next
          // one tries again (review 2026-10-01).
          if (err instanceof ErrorResponse) setUser(null)
          return null
        })
        .finally(() => {
          renewRef.current = null
        })
    }
    return renewRef.current
  }, [])
  renewOrExpireRef.current = renewOrExpire

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: !!user && !user.expired,
      isLoading,
      login,
      logout,
      getAccessToken,
      renewOrExpire,
    }),
    [user, isLoading, login, logout, getAccessToken, renewOrExpire],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
